import { equals, includes, map, pipe, prop, propOr } from 'ramda';

import { findBoolean } from './common';
import { BONUS_RESTRICTED_COUNTRIES, Country } from './conf';
import { User, UserStatus } from '@/api/auth';
import { Currency } from '@/api/common';

export enum USER_GROUP {
  VIP = 'VIP',
  SILVER = 'PVIP',
  GOLD = 'VIP1',
  PLATINUM = 'VIP2',
  DIAMOND = 'VIP3',
  DEPOSITOR = 'DEP',
  BA = 'BA',
  BR = 'BR',
  WPE = 'WPE',
  SignUps = '00002',
  ONE_OR_MORE_DEP = 'DEP1',
  ACTIVE_BONUS = 'ACTBONUS',
  LA = 'LA',
  REF = 'REF',
  LOYALTY_VIP = 'Loyalty_VIP',
  LOYALTY = 'Loyalty',
  SWEDISH = 'Swedish',
  AUD100 = '100aud',
  BONUS_HUNTERS = 'BH',
  JACKPOT_USERS = 'Jackpot_Users', // 5+ deposits
  FIAT_PLAYERS = 'FiatPlayers',
  VERIFIED = 'Tag_Verified',
  DEV_TEST = 'DevTest', //group for QA
  VIPBA = 'VIPBA',
  RAKEBACK = 'RAKEBACK',
  Loyals = 'Loyals',
  Champions = 'Champions',
  TSG_COMPLETE = 'TSG_COMPLETE',
  TSG_START = 'TSG_START',
  SUPER_SPOT = 'SVIP',
  Duplicate = 'Duplicate',
  DEP5 = 'DEP5',
}

export const COMMON_VIP_GROUPS = [
  USER_GROUP.GOLD,
  USER_GROUP.PLATINUM,
  USER_GROUP.DIAMOND,
];

export const vipGroups = [...COMMON_VIP_GROUPS, USER_GROUP.SILVER];

export const groupMember = (groupId: string) =>
  pipe<[User | undefined], UserStatus[], UserStatus['id'][], boolean>(
    propOr<UserStatus[]>([], 'statuses'),
    map(prop('id')),
    findBoolean(equals(groupId))
  );

export const everyGroupMember = (groupId: string[]) => (user?: User) =>
  map((id) => groupMember(id)(user), groupId).every((v) => !!v);

export const someGroupMember = (groupId: string[]) => (user?: User) =>
  map((id) => groupMember(id)(user), groupId).some((v) => !!v);

export const matchGroupMember = (groupId: string[]) => (user?: User) =>
  map((id) => groupMember(id)(user), groupId);

export const groupMemberNoDeposits = groupMember(USER_GROUP.SignUps);

export const groupMemberAUD100 = groupMember(USER_GROUP.AUD100);

export const groupMembeRakeback = groupMember(USER_GROUP.RAKEBACK);

export const groupMemberActiveBonus = groupMember(USER_GROUP.ACTIVE_BONUS);

export const groupMemberDepositor = someGroupMember([USER_GROUP.DEPOSITOR]);

export const groupMemberVIP = someGroupMember(vipGroups);

export const groupMemberSwedish = groupMember(USER_GROUP.SWEDISH);

export const groupMemberBA = groupMember(USER_GROUP.BA);

export const groupMemberSILVER = groupMember(USER_GROUP.SILVER);

export const groupMemberGOLD = groupMember(USER_GROUP.GOLD);

export const groupMemberPLATINUM = groupMember(USER_GROUP.PLATINUM);

export const groupMemberDIAMOND = groupMember(USER_GROUP.DIAMOND);

export const groupMemberJackpotUsers = groupMember(USER_GROUP.JACKPOT_USERS);

export const groupMemberVerified = groupMember(USER_GROUP.VERIFIED);

export const groupMemberDuplicate = groupMember(USER_GROUP.Duplicate);

export const isUserDataCompleted = (user?: User) => {
  return (
    !!user?.firstName &&
    !!user?.lastName &&
    !!user?.dateOfBirth &&
    !!user?.mobilePhone &&
    !!user?.country &&
    !!user?.city &&
    !!user?.address &&
    !!user?.postalCode
  );
};

export const checkCompleteAdditionalUserData = (
  user: User | undefined,
  currencies: Currency[],
  isUserCountryStateFieldCompleted: boolean
) =>
  (!isUserDataCompleted(user) || !isUserCountryStateFieldCompleted) &&
  currencies.find(({ code }) => code === user?.currency)?.fiat;

export const groupMemberBR = groupMember(USER_GROUP.BR);

function groupBuilder(group: USER_GROUP): UserStatus;
function groupBuilder(group: USER_GROUP[]): UserStatus[];
function groupBuilder(group: any) {
  if (Array.isArray(group)) {
    return group.map((g) => ({ id: g, name: g }));
  }

  return { id: group, name: group };
}

const newcomerGroups = groupBuilder([USER_GROUP.SignUps, USER_GROUP.WPE]);

const bonusRestrictedGroup = groupBuilder(USER_GROUP.BR);

export const bonusRestrictedGroups = [USER_GROUP.BA, USER_GROUP.BR];

export const promoRestrictedGroups = [
  ...bonusRestrictedGroups,
  USER_GROUP.VIPBA,
];

export const checkByRestrictedCountries = (country: string) =>
  includes(country as Country, BONUS_RESTRICTED_COUNTRIES);

export const setGroupsForNewcomer = (user?: User, countryCode?: string) => {
  if (!user) return;

  const statuses = user.statuses || [];
  const country = user.country || countryCode || null;

  const pushBonusRestrictedGroup = (groups: UserStatus[]) => [
    ...groups,
    bonusRestrictedGroup,
  ];

  const updatedStatuses =
    statuses.length === 0
      ? newcomerGroups
      : statuses
          .map((status) => {
            if (country && checkByRestrictedCountries(country)) {
              return pushBonusRestrictedGroup([status]);
            }
            return [status];
          })
          .flat();

  return { ...user, statuses: updatedStatuses };
};
